import React from 'react';
import { Button, Card } from 'react-bootstrap';
import {
  BloodworkAnalysis,
  FurtherConsiderations,
  Section,
} from '../../models';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import {
  parseSupplementItem,
  systemicFormulasProductUrl,
} from '../../utils/supplementItem';
import { scrollPanelIntoView } from '../../utils/scrollPanelIntoView';
import {
  SupplementsBlock,
  packSupplementBlocks,
} from '../../utils/pdfSupplementsPagination';
import {
  HOLOBIOME_KITS,
  SYSTEMIC_FORMULAS_BRAND,
  normalizeSupplementName,
} from '../../models/holobiomeKits';
import { prioritizeRootCauseCategories } from './further-considerations-section';
import {
  rankSectionsBySeverity,
  sectionShortLabel,
} from './results-overview-panel';

// Per-system PDF labels in canonical report order. Drives both the order of
// section blocks within each brand and the labels we show. Exported so the
// consolidated Section Notes panel gathers each section's note under the same
// friendly labels, in the same order, as this Supplements panel.
export const SECTION_HEADINGS: { section: Section; label: string }[] = [
  { section: Section.RBC,                           label: 'Blood Oxygen & Red Blood Cell Function' },
  { section: Section.IRON,                          label: 'Iron Status & Utilization' },
  { section: Section.WBC,                           label: 'Immune Activity' },
  { section: Section.PLATELETS,                     label: 'Blood Clotting Function' },
  { section: Section.GLUCOSE,                       label: 'Glucose Regulation & Metabolic Function' },
  { section: Section.KIDNEY,                        label: 'Kidney Function' },
  { section: Section.PROTEINS,                      label: 'Protein Status' },
  { section: Section.NUTRIENTS,                     label: 'Vitamin & Mineral Status' },
  { section: Section.ELECTROLYTES,                  label: 'Electrolyte Balance' },
  { section: Section.LIVER_BILIARY,                 label: 'Liver Function' },
  { section: Section.LIPIDS,                        label: 'Lipid Balance' },
  { section: Section.INFLAMMATION_OXIDATIVE_STRESS, label: 'Inflammation & Oxidative Stress' },
  { section: Section.THYROID,                       label: 'Thyroid Function' },
  { section: Section.STRESS_ADRENAL,                label: 'Stress & Adrenal Balance' },
  { section: Section.SEX_HORMONE,                   label: 'Hormone Balance' },
  { section: Section.PROSTATE,                      label: 'Prostate Function' },
];

const BRAND_LABELS: { [brand: string]: string } = {
  systemicFormulas: 'Systemic Formulas',
  bwhLabs: 'BWH Labs',
};
const brandLabel = (brand: string): string => BRAND_LABELS[brand] ?? brand;

// Fixed display order for brand blocks: Systemic Formulas first, then BWH Labs,
// then any other/unknown brand alphabetically. Brands not listed here sort
// after the known ones. Used instead of an alphabetical sort so BWH Labs sits
// below Systemic Formulas rather than above it.
const BRAND_ORDER: string[] = ['systemicFormulas', 'bwhLabs'];
function compareBrands(a: string, b: string): number {
  const ia = BRAND_ORDER.indexOf(a);
  const ib = BRAND_ORDER.indexOf(b);
  if (ia !== -1 && ib !== -1) return ia - ib;
  if (ia !== -1) return -1;
  if (ib !== -1) return 1;
  return brandLabel(a).localeCompare(brandLabel(b));
}

// A Supplements row's title reads "<Brand> — Supporting Supplement(s) for
// <Pattern>" (plural for multi-product brands like Systemic Formulas, singular
// for single-product ones like BWH Labs). Returns null when a title doesn't
// follow the convention, in which case the product simply carries no pattern
// attribution rather than a mangled one.
const PATTERN_TITLE_RE = / Supporting Supplements? for (.+)$/;
function patternNameFromTitle(title?: string): string | null {
  if (!title) return null;
  const match = PATTERN_TITLE_RE.exec(title);
  return match ? match[1].trim() : null;
}

// Which brands an instance of this table covers. The Supplements panel and the
// Holobiome Roadmap section render the SAME table over two disjoint brand sets:
// the roadmap owns Systemic Formulas — its kits, its products, its protocol —
// and Supplements holds every other brand, so a product appears in exactly one
// of them.
//
// The split happens AFTER collection, not by narrowing the brand list going in,
// so each brand's month sequencing comes out identical to what it was when the
// two shared one panel: collectByBrandAndSection groups by brand and
// assignAddOnMonths already runs per brand.
export type SupplementsBrandScope = 'systemicFormulas' | 'otherBrands';

const inScope = (brand: string, scope: SupplementsBrandScope): boolean =>
  (brand === SYSTEMIC_FORMULAS_BRAND) === (scope === 'systemicFormulas');

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
  // Defaults to the Supplements panel's half — every brand EXCEPT Systemic
  // Formulas, which the Holobiome Roadmap section renders instead.
  brandScope?: SupplementsBrandScope;
  // Card heading. Defaults to 'Supplements'; the roadmap's PDF card passes its
  // own name.
  title?: string;
  forPdf?: boolean;
  includeInPdf?: boolean;
  onIncludeInPdfToggle?: () => void;
  // Required for Edit-as-override. When absent, the Edit button is hidden.
  bloodworkEntryStore?: BloodworkEntryStore;
};

type State = {
  editing: boolean;
  draftText: string;
};

type Supplement = { name: string; description: string; link?: string };

// Which Holobiome kit a product ships in, if any. A client is on every kit, so
// a recommended product that sits inside one isn't a separate purchase — the
// panel says so rather than presenting it identically to a true add-on.
type KitRef = { month: number; name: string };

// A product appears ONCE in the whole panel, under the most concerning system
// that recommended it.
//
// `patternRefs` are 1-based indices into its own section's `patterns` legend —
// the numbers the Pattern column shows. Numbering rather than repeating the
// name is what keeps this readable: four of Glucose's products come from the
// same 50-character pattern, and printing it four times is the redundancy the
// per-row version had.
type ProductRow = Supplement & {
  patternRefs: number[];
  // Other systems that also recommended it, worst-first. Noted under the
  // pattern numbers so the cross-system fact survives listing the product once.
  alsoSections: Section[];
  kit: KitRef | null;
  // When to start this product, 1–5. A kit product takes its kit's month; every
  // other product is placed by assignAddOnMonths below.
  month: number;
};

// One block per system, in severity order — worst first.
type SectionGroup = {
  section: Section;
  // The legend, in the order this system's patterns were encountered. Index i
  // is cited as number i+1.
  patterns: string[];
  products: ProductRow[];
};

type BrandGroup = {
  brand: string;
  sections: SectionGroup[];
};

// Working shape during collection, before ownership and numbering are decided.
type Collected = Supplement & {
  sections: Section[];
  patternsBySection: Map<Section, string[]>;
  orderBySection: Map<Section, number>;
  kit: KitRef | null;
};

// product name → the kit it ships in, earliest month first when it's in more
// than one (ImmuneGenic is in both Prepare and Promote; the client meets it in
// Prepare). Built once — HOLOBIOME_KITS is a module constant.
const KIT_BY_PRODUCT: Map<string, KitRef> = (() => {
  const map = new Map<string, KitRef>();
  HOLOBIOME_KITS.forEach(kit => {
    kit.productNames.forEach(name => {
      const key = normalizeSupplementName(name);
      const existing = map.get(key);
      if (!existing || kit.month < existing.month) {
        map.set(key, { month: kit.month, name: kit.name });
      }
    });
  });
  return map;
})();

const kitFor = (name: string): KitRef | null =>
  KIT_BY_PRODUCT.get(normalizeSupplementName(name)) ?? null;

// The full system name, for the heading that opens each group — there's a whole
// row for it, so it can say "Glucose Regulation & Metabolic Function" rather
// than the compact word the Results Overview boxes use. The short form is still
// what the cramped spots get (the "also Immune" note in the Pattern column).
const SECTION_LABEL = new Map(
  SECTION_HEADINGS.map(({ section, label }) => [section, label] as const),
);
const sectionFullLabel = (section: Section): string =>
  SECTION_LABEL.get(section) ?? sectionShortLabel(section);

// The Holobiome Roadmap runs five months.
const MONTHS = 5;

// How many products that AREN'T in a kit to start in any one month. The whole
// point of sequencing is that a client doesn't open a box of sixteen bottles,
// so the cap is what actually does that work.
//
// PROVISIONAL — 3 is a placeholder until Systemic Formulas tells us what's
// reasonable to add on top of a full kit. Changing it re-flows the months and
// nothing else.
const ADD_ONS_PER_MONTH = 3;

// Give every product a start month.
//
// Kit products keep their kit's month and cost nothing against the cap — the
// client receives them that month regardless of what we say here.
//
// Everything else is placed by severity: `sections` arrives worst-first, so the
// products answering the most concerning system start in Month 1, the next
// system's in Month 2, and so on. Within that, a system with more add-ons than
// the cap spills forward month by month rather than dumping them all at once.
//
// NOT YET MODELLED: per-product clinical constraints ("don't introduce this one
// before Month 3"). Those are Mike's call and have no home in the sheet yet —
// see the "Add-on Mo" column. Until they exist, severity alone decides, which
// can place something later than a practitioner would like.
function assignAddOnMonths(sections: SectionGroup[]): void {
  const used = new Map<number, number>();
  sections.forEach((group, sectionIdx) => {
    const start = Math.min(sectionIdx + 1, MONTHS);
    group.products.forEach(product => {
      if (product.kit) {
        product.month = product.kit.month;
        return;
      }
      let month = start;
      while (month < MONTHS && (used.get(month) ?? 0) >= ADD_ONS_PER_MONTH) {
        month += 1;
      }
      used.set(month, (used.get(month) ?? 0) + 1);
      product.month = month;
    });
  });
}

// Plain-text form for the override seed. Unlike the rendered cell, this spells
// out "no kit" — the seed is prose a practitioner reads, where a trailing empty
// bracket would just look broken.
const kitLabel = (kit: KitRef | null): string =>
  kit ? `Holobiome Month ${kit.month}` : 'no kit';

// Thin alias over the shared parser so the rest of this file keeps reading
// parseItem(...). The separator/URL logic lives in src/utils/supplementItem.ts
// so this panel and FurtherConsiderationsSection can never drift apart.
function parseItem(item: string): Supplement | null {
  return parseSupplementItem(item);
}

// A product name, everywhere one is shown: bold, and a link when we have a URL
// for it. No color of its own — it takes Bootstrap's link color, which is what
// keeps every product name in the report the same blue. Exported so the
// roadmap's Add-On Products table renders its names through this too; the two
// tables sit one above the other, and hard-coding a hex in either is how they'd
// drift apart.
export function renderSupplementName(s: {
  name: string;
  link?: string;
}): JSX.Element {
  if (!s.link) return <strong>{s.name}</strong>;
  return (
    <a
      href={s.link}
      target="_blank"
      rel="noopener noreferrer"
      style={{ fontWeight: 'bold' }}
    >
      {s.name}
    </a>
  );
}

// Collect every recommended product once per brand, ordered by how concerning
// the system that recommended it is.
//
// `severityOrder` is the section ranking from the Results Overview (worst
// first). Sections missing from it — untested, or with no valued markers — sort
// after the ranked ones in canonical report order, so nothing silently
// disappears if the ranking can't see a section.
function collectByBrandAndSection(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
  severityOrder: Section[],
): BrandGroup[] {
  const byBrand = new Map<string, Map<string, Collected>>();
  // "<brand>::<section>" → how many distinct products that system has
  // contributed so far, i.e. the next product's position in its list.
  const positionInSection = new Map<string, number>();

  const severityRank = (section: Section): number => {
    const idx = severityOrder.indexOf(section);
    // Unranked sections go after every ranked one, then by report order.
    if (idx !== -1) return idx;
    const fallback = SECTION_HEADINGS.findIndex(h => h.section === section);
    return severityOrder.length + (fallback === -1 ? SECTION_HEADINGS.length : fallback);
  };

  // Walk in canonical report order; the severity sort below reorders the result.
  // Within a section, the same category order the Supplements tab uses, so the
  // first product a pattern lists is the first one seen here — that order is
  // the clinical priority and survives into the table.
  SECTION_HEADINGS.forEach(({ section }) => {
    const categories =
      bloodworkAnalysis.possibleRootCauseCategoriesFor?.(section) ?? [];
    prioritizeRootCauseCategories(section, categories).forEach(category => {
      const matches = furtherConsiderations.findConsiderations(
        category.rootCauseCategory,
        section,
        'Supplements',
        brands,
      );
      matches.forEach(consideration => {
        const brand = consideration.brand || 'unknown';
        const patternName = patternNameFromTitle(consideration.title);
        (consideration.items || []).forEach(item => {
          // resolveSupplement handles both a supplement-library tag (resolved
          // via findByTag) and a legacy inline "Name — Description — url"
          // string. Returns null for the "Use code …" promo, which must not
          // reach the panel as a product.
          const parsed = furtherConsiderations.resolveSupplement(item);
          if (!parsed?.name) return;

          if (!byBrand.has(brand)) byBrand.set(brand, new Map());
          const products = byBrand.get(brand)!;
          const key = normalizeSupplementName(parsed.name);

          // Next slot in this system's list, claimed the first time the system
          // mentions the product.
          const posKey = `${brand}::${section}`;
          const claimPosition = (row: Collected) => {
            if (row.orderBySection.has(section)) return;
            const next = positionInSection.get(posKey) ?? 0;
            row.orderBySection.set(section, next);
            positionInSection.set(posKey, next + 1);
          };

          const notePattern = (row: Collected) => {
            if (!patternName) return;
            const list = row.patternsBySection.get(section) ?? [];
            if (!list.includes(patternName)) list.push(patternName);
            row.patternsBySection.set(section, list);
          };

          const existing = products.get(key);
          if (existing) {
            // Same product, another pattern or another system: record the extra
            // origin rather than repeating the row.
            if (!existing.sections.includes(section)) {
              existing.sections.push(section);
            }
            claimPosition(existing);
            notePattern(existing);
            return;
          }
          const row: Collected = {
            name: parsed.name,
            description: parsed.description || '',
            // Every product name is a link. The catalog URL when the sheet
            // carries one; otherwise, for Systemic Formulas only, the slug
            // guess — so a product that hasn't been wired up yet still reaches
            // its page rather than sitting there as dead bold text. Another
            // brand's product without a URL stays plain, since guessing an SF
            // slug for it would link somewhere that doesn't sell it.
            link:
              parsed.link ||
              (brand === 'systemicFormulas'
                ? systemicFormulasProductUrl(parsed.name)
                : undefined),
            sections: [section],
            patternsBySection: new Map(),
            kit: kitFor(parsed.name),
            orderBySection: new Map(),
          };
          claimPosition(row);
          notePattern(row);
          products.set(key, row);
        });
      });
    });
  });

  // Ownership, grouping, and numbering. A product belongs to the most
  // concerning system that recommended it; each system then numbers only the
  // patterns its OWN products cite, so the legend never lists a pattern with
  // nothing under it.
  return Array.from(byBrand.entries())
    .map(([brand, collected]) => {
      const rows = Array.from(collected.values());
      rows.forEach(row =>
        row.sections.sort((a, b) => severityRank(a) - severityRank(b)),
      );

      const bySection = new Map<Section, Collected[]>();
      rows.forEach(row => {
        const owner = row.sections[0];
        const list = bySection.get(owner) ?? [];
        list.push(row);
        bySection.set(owner, list);
      });

      const sections: SectionGroup[] = Array.from(bySection.entries())
        .sort(([a], [b]) => severityRank(a) - severityRank(b))
        .map(([section, owned]) => {
          // Products in the order this system's patterns listed them — the
          // clinical priority.
          owned.sort(
            (a, b) =>
              (a.orderBySection.get(section) ?? 0) -
              (b.orderBySection.get(section) ?? 0),
          );

          const patterns: string[] = [];
          const products = owned.map(row => {
            const refs = (row.patternsBySection.get(section) ?? []).map(name => {
              let idx = patterns.indexOf(name);
              if (idx === -1) idx = patterns.push(name) - 1;
              return idx + 1; // 1-based, as displayed
            });
            return {
              name: row.name,
              description: row.description,
              link: row.link,
              patternRefs: refs,
              alsoSections: row.sections.slice(1),
              kit: row.kit,
              month: 0, // set by assignAddOnMonths once every group exists
            };
          });
          return { section, patterns, products };
        });

      assignAddOnMonths(sections);
      return { brand, sections };
    })
    .filter(g => g.sections.length > 0)
    .sort((a, b) => compareBrands(a.brand, b.brand));
}

// collectByBrandAndSection, narrowed to one half of the brand split. Note the
// severity ranking is computed from the whole analysis either way, so a system
// keeps the same position in both halves.
function scopedGroups(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
  scope: SupplementsBrandScope,
): BrandGroup[] {
  return collectByBrandAndSection(
    bloodworkAnalysis,
    furtherConsiderations,
    brands,
    rankSectionsBySeverity(bloodworkAnalysis),
  ).filter(group => inScope(group.brand, scope));
}

// Lightweight markdown-ish convention used by the override textarea. The
// rendered view parses these markers back into brand / section / bullet
// styling.
//   "## Brand"            → brand heading (h4)
//   "### Section"          → section heading (h6)
//   "- Name — Description" → bullet with bold name
//   anything else          → plain paragraph
//
// NOTE the generated view is a table while an edited one is a bullet list. That
// divergence is deliberate: an override is a free-text replacement the
// practitioner owns, and a list is what's actually editable by hand — there is
// no sane plain-text syntax for "product, description, systems, kit" that
// someone would want to maintain in a textarea. The seed text below carries the
// system and kit inline so nothing is lost when they start editing.
function groupsToText(groups: BrandGroup[]): string {
  return groups
    .map(({ brand, sections }) => {
      const blocks = sections
        .map(group => {
          const bullets = group.products
            .map(p => {
              const base = `- ${p.name} — ${p.description}`;
              const meta = `[Month ${p.month} · ${kitLabel(p.kit)}]`;
              return p.link ? `${base} ${meta} — ${p.link}` : `${base} ${meta}`;
            })
            .join('\n');
          return `### ${sectionShortLabel(group.section)}\n${bullets}`;
        })
        .join('\n\n');
      return `## ${brandLabel(brand)}\n\n${blocks}`;
    })
    .join('\n\n');
}

function renderOverride(override: string): JSX.Element {
  const lines = override.split('\n');
  const nodes: JSX.Element[] = [];
  let bullets: string[] = [];

  const flushBullets = () => {
    if (bullets.length === 0) return;
    nodes.push(
      <ul key={`ul-${nodes.length}`} style={{ marginBottom: 8 }}>
        {bullets.map((b, i) => {
          const supp = parseItem(b);
          return supp ? (
            <li key={i}>
              {renderSupplementName(supp)} — {supp.description}
            </li>
          ) : (
            <li key={i}>{b}</li>
          );
        })}
      </ul>,
    );
    bullets = [];
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (line.startsWith('- ')) {
      bullets.push(line.slice(2));
    } else if (trimmed.startsWith('### ')) {
      flushBullets();
      nodes.push(
        <h6
          key={`h6-${idx}`}
          style={{
            marginTop: nodes.length > 0 ? 14 : 0,
            marginBottom: 6,
            marginLeft: 8,
            fontSize: '1rem',
            fontWeight: 'bold',
          }}
        >
          {trimmed.slice(4)}
        </h6>,
      );
    } else if (trimmed.startsWith('## ')) {
      flushBullets();
      nodes.push(
        <h4
          key={`h4-${idx}`}
          style={{
            marginTop: nodes.length > 0 ? 18 : 0,
            marginBottom: 12,
            fontSize: '1.5rem',
          }}
        >
          <strong>{trimmed.slice(3)}</strong>
        </h4>,
      );
    } else if (trimmed === '') {
      flushBullets();
    } else {
      flushBullets();
      nodes.push(<div key={`p-${idx}`}>{line}</div>);
    }
  });
  flushBullets();

  return <>{nodes}</>;
}

// ---------------------------------------------------------------------------
// PDF pagination
//
// The PDF renders one Card per page with an explicit page break between them,
// rather than one tall Card that html2pdf slices wherever it lands. See
// pdfSupplementsPagination.ts for why a page-break-inside rule cannot do this
// job. Both content paths (auto-generated and the practitioner's edited
// override) funnel into the same block list so both paginate.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Table rendering — shared by the screen and the PDF so the two can't drift.
// ---------------------------------------------------------------------------

const CELL: React.CSSProperties = {
  padding: '4px 10px 4px 0',
  borderBottom: '1px solid #e9ecef',
  verticalAlign: 'middle',
};

// Short columns hold their content on one line. No overflow handling: these
// are sized to fit, so there is nothing to clip — only Description wraps.
const NOWRAP: React.CSSProperties = { whiteSpace: 'nowrap' };
const HEAD_CELL: React.CSSProperties = {
  padding: '0 10px 5px 0',
  borderBottom: '1px solid #ced4da',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: 'bold',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#6c757d',
  whiteSpace: 'nowrap',
};

// The system heading that opens each group, plus the numbered legend of the
// patterns its products cite. Spans the whole table so the legend has room to
// wrap — it's the one place a pattern name is spelled out, so it must not be
// squeezed into a column.
// Column widths, shared by the on-screen grouped table and the PDF's
// per-section slices so the two line up column for column.
// Sized so every column holds its content on one line: Product to the longest
// name, Pattern to "1, 2 · also Immune", Month to "Month 1 · Holobiome Kit",
// and Description takes everything left over.
const COL_WIDTHS = { product: 12, description: 57, pattern: 12, month: 19 };

const COLGROUP = (
  <colgroup>
    <col style={{ width: `${COL_WIDTHS.product}%` }} />
    <col style={{ width: `${COL_WIDTHS.description}%` }} />
    <col style={{ width: `${COL_WIDTHS.pattern}%` }} />
    <col style={{ width: `${COL_WIDTHS.month}%` }} />
  </colgroup>
);

// The legend sits in a row that spans the whole table, so left alone it runs
// out under Pattern and Month. Held to the Product + Description columns
// instead: the text ends where the descriptions below it end, and a legend too
// long for that wraps to the next line rather than reaching across the table.
const LEGEND_MAX_WIDTH = `${COL_WIDTHS.product + COL_WIDTHS.description}%`;

// 12px matches the Add-On Products table in the Protocols panel. The two are
// the same kind of object — a dense list of products with a description each —
// so they read as one system rather than two tables that happen to sit in the
// same report. Every body cell inherits this; only the section heading and its
// legend sit above it.
const TABLE_STYLE: React.CSSProperties = {
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  fontSize: '12px',
};

// The four column headings. Shared so the copy that opens each system on
// screen and the one at the top of each PDF slice can't drift apart.
const HEADER_CELLS = (
  <>
    <th style={HEAD_CELL}>Product</th>
    <th style={HEAD_CELL}>Description</th>
    <th style={{ ...HEAD_CELL, textAlign: 'center' }}>Pattern</th>
    <th style={HEAD_CELL}>Recommended Month</th>
  </>
);

// The numbered legend of a system's patterns — the one place a pattern name is
// spelled out. Everything else cites it by number, which is what stops a
// 50-character name repeating on four rows.
function PatternLegend({ patterns }: { patterns: string[] }): JSX.Element | null {
  if (patterns.length === 0) return null;
  return (
    // 13px rather than a caption size: this is the only place a pattern is
    // named, and every row in the group points back to it, so it has to be
    // comfortably readable — not fine print. Still a step under the product
    // rows so it reads as the heading's subtitle.
    <div
      style={{
        fontSize: '13px',
        lineHeight: 1.5,
        color: '#6c757d',
        maxWidth: LEGEND_MAX_WIDTH,
      }}
    >
      {patterns.map((pattern, i) => (
        // inline-block so an entry that won't fit moves to the next line whole,
        // instead of leaving its number stranded at the end of one line and its
        // name starting the next.
        <span
          key={pattern}
          style={{
            display: 'inline-block',
            marginRight: 14,
            verticalAlign: 'top',
          }}
        >
          <span style={{ fontWeight: 'bold', color: '#495057' }}>{i + 1}</span>{' '}
          {pattern}
        </span>
      ))}
    </div>
  );
}

// The system heading that opens each group. Plain black and bold rather than a
// jump link: the products under it are what the reader is here for, and a blue
// underlined heading competed with the product names — which ARE links — for
// the same "click me" signal.
function SectionHeading({ section }: { section: Section }): JSX.Element {
  return (
    <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#212529' }}>
      {sectionFullLabel(section)}
    </span>
  );
}

// One product row. Shared so the screen and the PDF can't drift.
//
// Description WRAPS. It was briefly clipped to an ellipsis to force every row
// onto one line, but catalog descriptions run to 230 characters — clipping hid
// most of the text behind a hover, which is the opposite of readable. The
// short columns stay on one line; the description sets the row height.
function productRow(p: ProductRow): JSX.Element {
  return (
    <tr key={p.name}>
      {/* No NOWRAP here, unlike the other short columns. At 12% the widest
          name ("Metabolic Superfood") only just fits, and on a narrow window it
          wouldn't — a fixed-layout cell that can't wrap overflows into its
          neighbour rather than clipping, so wrapping is the safe degradation. */}
      <td style={CELL}>{renderSupplementName(p)}</td>
      <td style={CELL}>{p.description}</td>
      {/* Numbers into the legend above, not the pattern names — several of a
          system's products share one 50-character pattern, and spelling it out
          per row is what made this redundant. */}
      <td style={{ ...CELL, ...NOWRAP, textAlign: 'center' }}>
        {p.patternRefs.join(', ')}
        {p.alsoSections.length > 0 && (
          <span style={{ color: '#8b96a3' }}>
            {' · also '}
            {p.alsoSections.map(sectionShortLabel).join(' · ')}
          </span>
        )}
      </td>
      {/* Month first, then what it is, on one line. "Holobiome Kit" means it
          arrives in that month's box; "Add-on" means it's a separate
          purchase. */}
      <td style={{ ...CELL, ...NOWRAP }}>
        Month {p.month}
        <span style={{ color: p.kit ? '#3f7a55' : '#8b96a3' }}>
          {' · '}
          {p.kit ? 'Holobiome Kit' : 'Add-on'}
        </span>
      </td>
    </tr>
  );
}

// On-screen table: every system in one table, each opening with a heading row
// that carries its link and its pattern legend.
function ProductTable({ sections }: { sections: SectionGroup[] }): JSX.Element {
  return (
    // Horizontal scroll on the table's own container, so a narrow viewport
    // never makes the whole report scroll sideways.
    <div style={{ overflowX: 'auto' }}>
      {/* Percentage widths on a fixed layout: the table fills the panel, but
          Description can't run away with the slack the way it did under auto
          layout — its share is capped, and Pattern/Month stay narrow because
          their content is short and fixed-shape. */}
      <table style={TABLE_STYLE}>
        {COLGROUP}
        {/* No <thead>: the column headings repeat inside each system instead,
            below that system's name and legend. A single set at the top of the
            table sat above the first system's heading, which read as though the
            heading were data under it. */}
        <tbody>
          {sections.map((group, groupIdx) => (
            <React.Fragment key={String(group.section)}>
              <tr>
                <td
                  colSpan={4}
                  style={{
                    borderBottom: 'none',
                    paddingTop: groupIdx === 0 ? 0 : 26,
                    paddingBottom: 8,
                  }}
                >
                  <SectionHeading section={group.section} />
                  <PatternLegend patterns={group.patterns} />
                </td>
              </tr>
              <tr>{HEADER_CELLS}</tr>
              {group.products.map(productRow)}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// PDF slice: the products of ONE system, with column headers but no group
// heading — the block's own label carries the system name, and repeats with
// "(cont.)" when the packer splits it across pages.
function PdfProductTable({ products }: { products: ProductRow[] }): JSX.Element {
  return (
    <table style={TABLE_STYLE}>
      {COLGROUP}
      <thead>
        <tr>{HEADER_CELLS}</tr>
      </thead>
      <tbody>{products.map(productRow)}</tbody>
    </table>
  );
}

// A product row (generated view), a bullet, or a stray line of prose. The last
// two only come from an edited override, which stays a list — see groupsToText.
type PdfItem = {
  key: string;
  row?: ProductRow;
  // The numbered pattern legend that opens a system's slice.
  legend?: string[];
  bullet?: JSX.Element;
  text?: string;
};

// Height estimates in html2pdf canvas px, measured off a rendered report at the
// PDF's ~800px content width. Pattern headings and product bullets both wrap to
// two lines more often than not, so these are sized for the two-line case:
// overestimating only costs a little whitespace, while underestimating puts
// content back in the footer.
const PDF_PAGE_CAPACITY = 900;
const PDF_CARD_OVERHEAD = 76;
const PDF_BRAND_HEADING = 48;
const PDF_PATTERN_HEADING = 62;
// A table row now carries a pattern name under its system as well as the
// description, so it runs taller than the old single-line bullet.
const PDF_ITEM_HEIGHT = 46;

// One block per brand, holding every product row. The packer splits a block
// that overflows a page and flags the continuation, so a long list breaks
// between rows and repeats its header — the same treatment the old per-pattern
// blocks got, minus the pattern headings that no longer exist.
// One block per (brand, system). The packer splits a system that overflows a
// page and repeats its label with "(cont.)", so a long list breaks between
// products under a heading that still says which system they belong to.
//
// `showBrandHeadings` is off for the single-brand Holobiome scope, where the
// card header already names the brand.
function blocksFromGroups(
  groups: BrandGroup[],
  showBrandHeadings: boolean,
): SupplementsBlock<PdfItem>[] {
  const blocks: SupplementsBlock<PdfItem>[] = [];
  groups.forEach(({ brand, sections }) => {
    sections.forEach((group, idx) => {
      blocks.push({
        key: `${brand}-${group.section}`,
        // Charged to the brand's first system only, so the brand heading
        // travels with it and can't be stranded at the foot of a page.
        brandLabel: idx === 0 && showBrandHeadings ? brandLabel(brand) : null,
        label: sectionShortLabel(group.section),
        items: [
          ...(group.patterns.length > 0
            ? [{ key: `${group.section}-legend`, legend: group.patterns }]
            : []),
          ...group.products.map(p => ({ key: p.name, row: p })),
        ],
        isContinuation: false,
      });
    });
  });
  return blocks;
}

// Same markdown-ish convention renderOverride parses, mapped onto blocks so an
// edited panel paginates exactly like a generated one. A brand heading with no
// pattern under it, or bullets with no heading, still produce a valid block.
function blocksFromOverride(override: string): SupplementsBlock<PdfItem>[] {
  const blocks: SupplementsBlock<PdfItem>[] = [];
  let pendingBrand: string | null = null;
  let current: SupplementsBlock<PdfItem> | null = null;

  const open = (label: string | null, key: string) => {
    current = {
      key,
      brandLabel: pendingBrand,
      label,
      items: [],
      isContinuation: false,
    };
    pendingBrand = null;
    blocks.push(current);
  };

  override.split('\n').forEach((line, idx) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('## ')) {
      // Hold the brand until the next pattern heading so the two stay welded.
      if (pendingBrand !== null) open(null, `brand-${idx}`);
      pendingBrand = trimmed.slice(3);
      current = null;
    } else if (trimmed.startsWith('### ')) {
      open(trimmed.slice(4), `pattern-${idx}`);
    } else if (line.startsWith('- ')) {
      if (!current) open(null, `items-${idx}`);
      const raw = line.slice(2);
      const supp = parseItem(raw);
      current!.items.push(
        supp
          ? {
              key: `b-${idx}`,
              bullet: (
                <>
                  {renderSupplementName(supp)} — {supp.description}
                </>
              ),
            }
          : { key: `b-${idx}`, bullet: <>{raw}</> },
      );
    } else if (trimmed !== '') {
      if (!current) open(null, `text-${idx}`);
      current!.items.push({ key: `t-${idx}`, text: line });
    }
  });

  if (pendingBrand !== null) open(null, 'brand-trailing');
  return blocks;
}

// Consecutive product rows run together into one table; consecutive bullets
// into one <ul>; a prose line breaks whichever run is open. Mixed content only
// arises from an edited override.
function renderPdfItems(items: PdfItem[]): JSX.Element[] {
  const out: JSX.Element[] = [];
  let rows: ProductRow[] = [];
  let bullets: PdfItem[] = [];

  const flushRows = () => {
    if (rows.length === 0) return;
    out.push(<PdfProductTable key={`tbl-${out.length}`} products={rows} />);
    rows = [];
  };
  const flushBullets = () => {
    if (bullets.length === 0) return;
    out.push(
      <ul key={`ul-${out.length}`} style={{ marginBottom: 0 }}>
        {bullets.map(i => (
          <li key={i.key}>{i.bullet}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };

  items.forEach(i => {
    if (i.legend) {
      flushRows();
      flushBullets();
      out.push(
        <div key={i.key} style={{ marginBottom: 6 }}>
          <PatternLegend patterns={i.legend} />
        </div>,
      );
    } else if (i.row) {
      flushBullets();
      rows.push(i.row);
    } else if (i.bullet) {
      flushRows();
      bullets.push(i);
    } else {
      flushRows();
      flushBullets();
      out.push(<div key={i.key}>{i.text}</div>);
    }
  });
  flushRows();
  flushBullets();
  return out;
}

function renderPdfBlock(
  block: SupplementsBlock<PdfItem>,
  isFirstOnPage: boolean,
): JSX.Element {
  return (
    <React.Fragment key={block.key}>
      {block.brandLabel && (
        <h4
          style={{
            marginTop: isFirstOnPage ? 0 : 18,
            marginBottom: 12,
            fontSize: '1.5rem',
          }}
        >
          <strong>{block.brandLabel}</strong>
        </h4>
      )}
      <div style={{ marginBottom: 14, marginLeft: 8 }}>
        {block.label !== null && (
          <h6
            style={{
              marginBottom: 6,
              fontSize: '1rem',
              fontWeight: 'bold',
            }}
          >
            {block.label}
            {block.isContinuation ? ' (cont.)' : ''}
          </h6>
        )}
        {renderPdfItems(block.items)}
      </div>
    </React.Fragment>
  );
}

function readOverride(bloodworkAnalysis: BloodworkAnalysis): string {
  const note = bloodworkAnalysis?.bloodwork?.bloodworkNotes?.findNote(
    Section.SUPPLEMENTS,
  );
  return (note?.text ?? '').trimStart();
}

export class SupplementsSummaryPanel extends React.Component<Props, State> {
  state: State = {
    editing: false,
    draftText: '',
  };

  private editTextareaEl: HTMLTextAreaElement | null = null;

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (this.state.editing && !prevState.editing) {
      this.fitTextareaToContent();
    }
  }

  private setEditTextareaRef = (el: HTMLTextAreaElement | null) => {
    this.editTextareaEl = el;
  };

  private fitTextareaToContent = () => {
    const el = this.editTextareaEl;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflow = 'hidden';
  };

  private get brandScope(): SupplementsBrandScope {
    return this.props.brandScope ?? 'otherBrands';
  }

  private computeGroups(): BrandGroup[] {
    const { bloodworkAnalysis, furtherConsiderations, enabledSupplementBrands } =
      this.props;
    return scopedGroups(
      bloodworkAnalysis,
      furtherConsiderations,
      enabledSupplementBrands,
      this.brandScope,
    );
  }

  // The practitioner's free-text override belongs to the Supplements panel —
  // the instance that owns the Edit button and seeds the draft. The Holobiome
  // scope never reads it, or the same override text would print twice: once
  // under the roadmap and again under Supplements.
  private readScopedOverride(): string {
    if (this.brandScope === 'systemicFormulas') return '';
    return readOverride(this.props.bloodworkAnalysis);
  }

  // Rough height (in px, html2pdf canvas units) of the rendered PDF panel.
  // Used by the parent container to decide whether the Disclaimer card fits
  // below Supplements on the same page or needs to break to its own page.
  // Errs on the high side — overestimating just pushes the disclaimer to a
  // fresh page when it might have fit; underestimating risks overflow.
  static estimatedPdfHeight(
    bloodworkAnalysis: BloodworkAnalysis,
    furtherConsiderations: FurtherConsiderations,
    enabledSupplementBrands?: string[],
    brandScope: SupplementsBrandScope = 'otherBrands',
  ): number {
    if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return 0;
    // Same rule as readScopedOverride: only the Supplements half can be
    // overridden, so only it estimates from the override text.
    const override =
      brandScope === 'systemicFormulas' ? '' : readOverride(bloodworkAnalysis);
    const CARD_OVERHEAD = 100;
    if (override) {
      let h = CARD_OVERHEAD;
      override.split('\n').forEach(line => {
        const t = line.trim();
        if (t.startsWith('## ')) h += 60;
        else if (t.startsWith('### ')) h += 40;
        else if (line.startsWith('- ')) h += 26;
        else if (t === '') h += 8;
        else h += 24;
      });
      return h;
    }
    const groups = scopedGroups(
      bloodworkAnalysis,
      furtherConsiderations,
      enabledSupplementBrands,
      brandScope,
    );
    if (groups.length === 0) return 0;
    let h = CARD_OVERHEAD;
    groups.forEach(({ sections }) => {
      h += 65; // brand heading
      sections.forEach(({ patterns, products }) => {
        h += 40; // system heading
        h += patterns.length * 18; // legend
        h += 24; // table header row
        h += products.length * PDF_ITEM_HEIGHT;
      });
    });
    return h;
  }

  private handleEditClick = () => {
    const override = this.readScopedOverride();
    const draft = override || groupsToText(this.computeGroups());
    this.setState({ editing: true, draftText: draft });
  };

  private handleCancel = () => {
    this.setState({ editing: false, draftText: '' });
  };

  private writeNote = (text: string) => {
    const notes = this.props.bloodworkAnalysis.bloodwork.bloodworkNotes;
    let note = notes.findNote(Section.SUPPLEMENTS);
    if (!note) note = notes.addNote(Section.SUPPLEMENTS, text);
    note.text = text;
  };

  // This panel flips from edit mode (tall textarea) to view mode, which shifts
  // layout while the global spinner modal is up — and that modal locks body
  // scrolling, so restoring a captured scrollY underneath it was clamped to 0
  // and left the report at the very top. Land back at the top of this panel
  // instead, once the save has resolved and the spinner is gone.
  private scrollBackToPanel = () => {
    scrollPanelIntoView('supplements-summary');
  };

  private handleSave = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    const { bloodworkAnalysis, bloodworkEntryStore } = this.props;
    if (!bloodworkEntryStore) return;
    this.writeNote(this.state.draftText);
    bloodworkEntryStore
      .saveBloodwork(bloodworkAnalysis.bloodwork)
      .then(this.scrollBackToPanel)
      .catch(err => {
        console.error('Failed to save Supplements override', err);
        alert('Failed to save your changes. Please try again.');
        this.scrollBackToPanel();
      });
    this.setState({ editing: false, draftText: '' });
  };

  private handleReset = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    const { bloodworkAnalysis, bloodworkEntryStore } = this.props;
    if (!bloodworkEntryStore) return;
    if (
      !window.confirm(
        'Clear your edits and restore the auto-generated Supplements list?',
      )
    ) {
      return;
    }
    this.writeNote('');
    bloodworkEntryStore
      .saveBloodwork(bloodworkAnalysis.bloodwork)
      .then(this.scrollBackToPanel)
      .catch(err => {
        console.error('Failed to reset Supplements override', err);
        alert('Failed to reset. Please try again.');
        this.scrollBackToPanel();
      });
    this.setState({ editing: false, draftText: '' });
  };

  render() {
    const {
      bloodworkAnalysis,
      furtherConsiderations,
      title = 'Supplements',
      forPdf,
      includeInPdf,
      onIncludeInPdfToggle,
      bloodworkEntryStore,
    } = this.props;
    const { editing, draftText } = this.state;

    if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) {
      return null;
    }

    const groups = this.computeGroups();
    const override = this.readScopedOverride();
    // One brand in the Holobiome scope, and the card header already names it,
    // so the brand heading would just repeat it.
    const showBrandHeadings = this.brandScope !== 'systemicFormulas';

    if (groups.length === 0 && !override) {
      return null;
    }

    const generatedBody = (
      <>
        {groups.map(({ brand, sections }) => (
          <div key={brand} style={{ marginBottom: 20 }}>
            {showBrandHeadings && (
              <h4 style={{ marginBottom: 12, fontSize: '1.5rem' }}>
                <strong>{brandLabel(brand)}</strong>
              </h4>
            )}
            <ProductTable sections={sections} />
          </div>
        ))}
      </>
    );

    const body = override ? renderOverride(override) : generatedBody;

    if (forPdf) {
      // Pre-paginated: one Card per page with an explicit break between them.
      // html2pdf's own keep-together pass can't be trusted this deep into a
      // long report — its page grid has drifted a full text line away from the
      // grid the canvas is actually sliced on — so the panel decides its own
      // page boundaries the way every other long section here does.
      const pages = packSupplementBlocks<PdfItem>({
        blocks: override
          ? blocksFromOverride(override)
          : blocksFromGroups(groups, showBrandHeadings),
        pageCapacity: PDF_PAGE_CAPACITY,
        cardOverhead: PDF_CARD_OVERHEAD,
        brandHeadingHeight: PDF_BRAND_HEADING,
        patternHeadingHeight: PDF_PATTERN_HEADING,
        itemHeight: PDF_ITEM_HEIGHT,
      });

      return (
        <>
          {pages.map((blocks, pageIdx) => (
            <React.Fragment key={`supplements-page-${pageIdx}`}>
              {pageIdx > 0 && (
                <>
                  <div className="html2pdf__page-break" />
                  {/* Same top buffer the container puts before the first page:
                      a forced break lands a hair above the true page edge, and
                      without the spacer the card header gets clipped. */}
                  <div style={{ height: 28 }} aria-hidden />
                </>
              )}
              <Card className="disclaimer-panel mb-5">
                <Card.Header className="py-1">
                  {pageIdx === 0 ? title : `${title} (cont.)`}
                </Card.Header>
                <Card.Body
                  className="disclaimer-body note-card-body"
                  style={{ paddingBottom: 12 }}
                >
                  {blocks.map((block, i) => renderPdfBlock(block, i === 0))}
                </Card.Body>
              </Card>
            </React.Fragment>
          ))}
        </>
      );
    }

    // The viewed body is wrapped in a bordered box so the Edit button below
    // it visually anchors to the content (mirrors the textarea+Save layout
    // used by the Insights notes pattern above this panel).
    const viewBody = (
      <>
        <div
          style={{
            border: '1px solid #ced4da',
            borderRadius: 4,
            padding: 12,
            backgroundColor: '#fff',
          }}
        >
          {body}
        </div>
        {bloodworkEntryStore && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <Button
              type="button"
              variant="primary"
              title="Edit Supplements"
              onClick={this.handleEditClick}
            >
              Edit
            </Button>
            {override && (
              <Button
                type="button"
                variant="outline-danger"
                title="Discard your edits and restore the auto-generated list with clickable supplement names"
                onClick={this.handleReset}
              >
                Reset to default
              </Button>
            )}
          </div>
        )}
      </>
    );

    const editBody = (
      <>
        <div
          style={{
            fontSize: '12px',
            color: '#6c757d',
            marginBottom: 6,
          }}
        >
          Formatting:&nbsp;
          <code>## Brand</code>&nbsp;for brand headings,&nbsp;
          <code>### Section</code>&nbsp;for section headings.
        </div>
        <textarea
          ref={this.setEditTextareaRef}
          value={draftText}
          onChange={e => this.setState({ draftText: e.target.value }, this.fitTextareaToContent)}
          rows={24}
          style={{
            width: '100%',
            // Same size as the panel it turns back into on Save (the app's body
            // text is 12px) — a textarea inherits neither font nor size.
            fontFamily: 'inherit',
            fontSize: '12px',
            padding: 8,
            borderRadius: 4,
            border: '1px solid #ced4da',
          }}
        />
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <Button variant="primary" onClick={this.handleSave}>
            Save
          </Button>
          <Button variant="secondary" onClick={this.handleCancel}>
            Cancel
          </Button>
          {override && (
            <Button
              variant="outline-danger"
              onClick={this.handleReset}
              style={{ marginLeft: 'auto' }}
              title="Discard your edits and restore the auto-generated list"
            >
              Reset to default
            </Button>
          )}
        </div>
      </>
    );

    return (
      // Anchored so Save can scroll back to the top of this panel.
      <Card id="supplements-summary">
        <Card.Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>{title}</span>
          {!editing && onIncludeInPdfToggle && (
            <label
              htmlFor="include-in-pdf-supplements"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                margin: 0,
                fontWeight: 'normal',
                cursor: 'pointer',
                color: '#fff',
              }}
              title="Include this section in the Analysis PDF"
            >
              <span style={{ marginRight: '6px' }}>Include in PDF</span>
              <input
                id="include-in-pdf-supplements"
                type="checkbox"
                className="lsm-styled-checkbox"
                checked={!!includeInPdf}
                onChange={onIncludeInPdfToggle}
              />
            </label>
          )}
        </Card.Header>
        <Card.Body>{editing ? editBody : viewBody}</Card.Body>
      </Card>
    );
  }
}

// The Systemic Formulas half of the table, stripped of the Card chrome, the
// brand heading, and the Edit/override machinery — what the Holobiome Roadmap
// section drops in under its kit cards. Same collector, same columns, same
// month sequencing as the Supplements panel; the two just render disjoint
// halves of one list, so a product is never in both.
//
// This is where the roadmap's non-kit recommendations now live. They used to
// have their own "Add-On Products" table in the protocols panel, which this
// table subsumes: it carries the same product, description and origin, plus the
// pattern citation and the start month, and flags a non-kit product as "Add-on"
// in its Month cell.
export const HolobiomeProductTable: React.FC<{
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
}> = ({ bloodworkAnalysis, furtherConsiderations, enabledSupplementBrands }) => {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return null;
  const groups = scopedGroups(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
    'systemicFormulas',
  );
  if (groups.length === 0) return null;

  return (
    <div
      style={{
        width: '100%',
        marginTop: '4px',
        borderTop: '1px solid #dee2e6',
        paddingTop: '10px',
      }}
    >
      <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#212529' }}>
        Pattern-Identified Supplements
      </div>
      <div
        style={{
          fontSize: '12px',
          color: '#6c757d',
          lineHeight: 1.2,
          marginBottom: '8px',
        }}
      >
        Every Systemic Formulas product the patterns identified in this test
        point to, grouped by system, worst first. Kit products arrive in that
        month&apos;s box; add-ons are a separate purchase.
      </div>
      {groups.map(({ brand, sections }) => (
        <ProductTable key={brand} sections={sections} />
      ))}
    </div>
  );
};
